import Box from 'assets/dynamic-bundle/box.svg';
import cn from 'classnames';
import { useGetBundleName } from 'components/dynamic-bundle/hooks/use-get-bundle-name';
import { PriceComposition } from 'components/price-composition';
import { useCart } from 'contexts/cart';
import { buildFingerPrint } from 'contexts/cart/utils/build-fingerprint';
import { toCartItem } from 'contexts/cart/utils/to-cart-item';
import { EffectiveCartGifts } from 'interfaces/cart';
import {
  BundleGroup,
  getBundleTotals,
  groupBundleItems,
} from 'modules/cart/components/cart-items/cart-bundle/utils';
import EmptyCart from 'modules/cart/components/empty-cart';
import TinyCartItem from 'modules/cart/components/tiny-cart/item';
import { MinimalCheckoutItem } from 'modules/checkout/order';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { twMerge } from 'tailwind-merge';
import { Gtm } from 'tracking';
import { hasBundles } from 'utils/dynamic-bundle';
import IconCart from '../../../../assets/icons/cart.svg';
import {
  BasicPdProductItem,
  isGiftProductItem,
  PdProductItem,
} from '../../../../interfaces/line-item';
import Item from './item';

interface PropTypes {
  className?: string;
  items: Array<PdProductItem | MinimalCheckoutItem>;
  gifts?: EffectiveCartGifts['items'];
  canRemoveProducts?: boolean;
}

const ItemsRenderer: FC<{
  items: (BasicPdProductItem | MinimalCheckoutItem | BundleGroup)[];
}> = ({ items }) => {
  const { discountOrigins } = useCart();
  const { formatMessage } = useIntl();
  const getBundleName = useGetBundleName();
  return items.map((item, index) => {
    if ('bundleItems' in item) {
      const { totalWeight, totalDefaultPrice, totalPrice } = getBundleTotals({
        items: item.bundleItems,
        discountOrigins,
      });
      const { bundleName } = getBundleName(item.bundleId);

      return (
        <TinyCartItem
          className="bg-background-paper p-0"
          data-test={`tiny-cart-item-bundle-${item.bundleId}`}
          key={`${item.bundleId}-${index}`}
          title={bundleName}
          price={
            <PriceComposition
              bold={false}
              className="flex flex-col gap-0"
              currentPrice={totalPrice}
              priceBefore={totalDefaultPrice}
              size="md"
              cents
            />
          }
          description={[
            {
              content: `${formatMessage({ id: 'express-checkout:cart-items:size' })}: ${totalWeight}g`,
              id: 'checkout-summary-item-variant',
            },
          ]}
          quantity={item.quantity}
          image={
            <Box
              className="size-16"
              role="img"
              aria-label={formatMessage({ id: 'dynamic-bundle:illustration' })}
            />
          }
        />
      );
    }

    return <Item key={index} item={item} />;
  });
};

const CartItems: React.FC<PropTypes> = ({ className, items, canRemoveProducts = false, gifts }) => {
  const { updateCart } = useCart();

  const pdProductItems = useMemo(() => items.filter(isPdProductItem), [items]);
  const minimalCheckoutItems = useMemo(
    () => items.filter((item) => !isPdProductItem(item)),
    [items]
  );
  const groupedItems = useMemo(() => groupBundleItems(pdProductItems), [pdProductItems]);
  const allFilteredItems = useMemo(
    () => [...groupedItems, ...minimalCheckoutItems],
    [groupedItems, minimalCheckoutItems]
  );

  const onRemoveItem = useCallback(
    (item: PdProductItem | MinimalCheckoutItem) => {
      if (!isPdProductItem(item)) return;

      if (isGiftProductItem(item)) {
        updateCart([
          {
            target: toCartItem({ item, shouldQuantity: false }),
            updateData: {
              points: item.points,
              variantId: String(item.variantId),
              quantity: 0,
              redeemItem: true,
            },
          },
        ]);
      } else {
        updateCart([
          {
            target: toCartItem({ item, shouldQuantity: false }),
            updateData: {
              variantId: String(item.variantId),
              quantity: 0,
            },
          },
        ]);
      }
    },
    [updateCart]
  );

  useEffect(() => {
    if (hasBundles(pdProductItems)) {
      Gtm.track.selectElement({
        element: ['dynamic_bundle', 'start_checkout'],
      });
    }
  }, [pdProductItems]);

  return (
    <div className={cn(twMerge('flex flex-col gap-4', className))}>
      <h2 className="text-primary-main hidden text-xl font-black lg:flex">
        <IconCart className="mr-2 h-6 w-5" />
        <FormattedMessage id="checkout:summary:cart-items:title" />
      </h2>

      {allFilteredItems.length === 0 && <EmptyCart />}

      <div className="flex flex-col gap-6">
        <ItemsRenderer items={allFilteredItems} />

        {gifts
          ?.filter((v) => v.selected)
          .map((v) => v.variant)
          .map((item) => (
            <Item
              isCartGift
              key={buildFingerPrint(toCartItem({ item }))}
              item={item}
              canRemoveProducts={canRemoveProducts}
              onRemoveItem={() => onRemoveItem(item)}
            />
          ))}
      </div>
    </div>
  );
};

const isPdProductItem = (item: PdProductItem | MinimalCheckoutItem): item is PdProductItem => {
  return 'variantId' in item;
};

export default CartItems;
